<template>
  <!-- Nothing at all until the server has said whether this person may leave, and nothing for
       someone it says is not a member (an old API answers the same way). Its own card, not the
       danger zone's: leaving affects only you, and that card is about the whole workspace. -->
  <section class="fts-settings-card" v-if="check && check.reason !== 'not_member'">
    <div>
      <h2>Your membership</h2>
      <p class="fts-settings-card-sub">You, in this workspace.</p>
    </div>
    <div class="fts-settings-row">
      <div class="fts-settings-row-text">
        <strong>Leave this workspace</strong>
        <span v-if="check.canLeave">
          You lose access until a workspace admin adds you back. Nothing you made is deleted.
        </span>
        <span v-else>{{ check.message }}</span>
      </div>
      <button
        v-if="check.canLeave"
        type="button"
        class="fts-settings-btn is-danger"
        :disabled="busy"
        @click="leave"
      >
        {{ busy ? "Leaving…" : "Leave" }}
      </button>
    </div>
  </section>
</template>

<script>
/**
 * "Leave this workspace" — the person's own way out, in a workspace member's Overview and in the
 * danger zone of General for an admin.
 *
 * The server decides (WorkspaceLeaveRules): GET api/Workspace/LeaveCheck says up front whether
 * this person may leave and, if not, why — the owner cannot, and neither can the last workspace
 * admin — so they are told before they try rather than after. Leaving switches their membership
 * off exactly as an admin's disable does, which is why the copy can promise "until a workspace
 * admin adds you back": that is the same switch, turned on again.
 *
 * On success it emits `left`; the settings frame hands that to the shell, which moves the person
 * to another workspace just as it does after a delete.
 */
import { commonStore } from "../../store/commonStore";
import { clientStore } from "../../store/clientStore";

export default {
  name: "SettingsLeaveWorkspace",
  props: {
    workspaceId: [String, Number],
  },
  data() {
    return {
      check: null,
      busy: false,
    };
  },
  computed: {
    workspaceName() {
      return clientStore.getters.workspaceName || "this workspace";
    },
  },
  methods: {
    api() {
      return commonStore.state.client;
    },
    async load() {
      this.check = null;
      if (!this.workspaceId) return;
      try {
        const res = await this.api().get("/api/Workspace/LeaveCheck", {
          params: { workspaceId: this.workspaceId },
        });
        this.check = res.data || null;
      } catch (err) {
        // An API without the check has no way to leave either: offer nothing.
        this.check = null;
      }
    },
    async leave() {
      if (this.busy || !this.check || !this.check.canLeave) return;
      const name = this.workspaceName;
      const confirmed = await this.$confirmation.show({
        type: "confirm",
        title: "Leave " + name + "?",
        text:
          "You'll lose access to its records, forms and automations until a workspace admin adds you back. " +
          "Nothing you made is deleted.",
        buttons: [
          { title: "Cancel", bind: { text: true } },
          {
            title: "Leave workspace",
            bind: { color: "danger" },
            default: true,
            handler: ({ resolve }) => resolve(true),
          },
        ],
      });
      if (confirmed !== true) return;

      this.busy = true;
      try {
        await this.api().post("/api/Workspace/Leave", { workspaceId: Number(this.workspaceId) });
        this.$toasted.success("You left " + name + ".");
        this.$emit("left", this.workspaceId);
      } catch (err) {
        const data = err && err.response && err.response.data;
        if (data && data.error && data.error !== "not_member") {
          // Something changed since the check (say, the other admin left first): say why here.
          this.check = { canLeave: false, reason: data.error, message: data.message };
        }
        this.$toasted.global.error(err, (data && data.message) || "You could not leave this workspace.");
      } finally {
        this.busy = false;
      }
    },
  },
  watch: {
    workspaceId() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
};
</script>
